import React from 'react';
import { css } from '@emotion/css';
import BannerImage from './images/banner@1x.jpg';
import { useGetUserAccountDetails } from '@/apiHooks/userAccount.hook';
import { GRAY100 } from '@/domain/colors';
import { useIntl } from '@/i18n/hooks/useIntl';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const styles = {
  content: {
    background: 'rgb(51, 51, 51)',
    color: `${GRAY100}`,
    maxHeight: 'calc(100% - 300px)',
    minHeight: '245px',
    height: '45%',
    '@media (min-width: 576px)': {
      height: 'calc(100% - 340px)',
    },
  },
  title: {
    textAlign: 'center',
    paddingTop: 'var(--spacing-1000)',
    paddingBottom: 'var(--spacing-600)',
    marginBottom: '0',
    color: 'inherit',
  },
  description: {
    textAlign: 'center',
    paddingBottom: 'var(--spacing-1000)',
    color: 'var(--color-text-white)',
  },
  image: {
    background: `url(${BannerImage}) no-repeat`,
    minHeight: '300px',
    maxHeight: 'calc(100% - 245px)',
    height: '55%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '@media (min-width: 576px)': {
      height: '340px',
    },
  },
  button: {
    marginBottom: '25px',
    '@media (min-width: 576px)': {
      marginBottom: '30px',
    },
  },
  modal: {
    maxWidth: '760px',
    position: 'relative',
    backgroundColor: 'rgb(51, 51, 51)',
    flex: 1,
    overflowY: 'auto',
    zIndex: 0,
    '@media (max-width: 767px)': {
      height: '100%',
    },
    '@media screen and (max-height: 710px)': {
      height: '100%',
    },
  },
  divider: {
    borderColor: '#484848 !important',
  },
} as const;

interface BannerProps {
  variant: 'mieten' | 'wohnen';
  onClose: () => void;
}

export const Banner = ({ onClose, variant }: BannerProps) => {
  const { userAccountDetails } = useGetUserAccountDetails();
  const { t } = useIntl();

  const welcomeText = userAccountDetails?.firstName
    ? t(
        { id: 'welcomeModal.banner.welcomeText', defaultMessage: 'Herzlichen Glückwunsch' },
        { FirstName: userAccountDetails.firstName },
      )
    : t({ id: 'welcomeModal.banner.welcomeTextNoName', defaultMessage: 'Herzlichen Glückwunsch' });

  const descriptionText = t(
    { id: 'welcomeModal.banner.title', defaultMessage: 'Ab sofort kannst du alle Vorteile nutzen!' },
    { BuyerPlusLogo: <PlusBadge variant={variant === 'mieten' ? 'rent' : 'living'} whiteLogo={true} /> },
  );

  return (
    <div data-testid='bannerModal' className={`${css(styles.modal)}`}>
      <div className={`${css(styles.image)}`} />
      <div className={`grid-flex flex-column grid-justify-center grid-align-center ${css(styles.content)}`}>
        <h3 className={`${css(styles.title)}`} data-test-ctx='welcome-profile-test'>
          {welcomeText}
        </h3>
        <p
          className={`${css(styles.description)}`}
          data-test-ctx={variant === 'mieten' ? 'mieterplus-test' : 'wohnenplus-test'}
        >
          {descriptionText}
        </p>
        <button className={`${css(styles.button)} button-rounded button-primary`} onClick={onClose}>
          {t({ id: 'common.buttons.next', defaultMessage: 'Weiter' })}
        </button>
      </div>
    </div>
  );
};
